import React from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';

import {loadingMessageText, seeMoreEventsText} from '@stubhub/react-event-helper';
import EventList from '@stubhub/react-event-list';
import FormattedLink from '@stubhub/react-formatted-link';
import Panel from '@stubhub/react-panel';
import StoreProvider, {ssr_wait} from '@stubhub/react-store-provider';
import urgency from '@stubhub/react-urgency-decorator';

import EventSorter from './event-sorter';
import SellListPanelLayout from './layouts/sell-event-list-panel-layout';
import {countryCodes} from './utils/constants';

import './index.scss';

export class EventListPanel extends React.Component {
  constructor(props) {
    super(props);
    this.handleEventItemCheckboxClick = this.handleEventItemCheckboxClick.bind(this);
    this.applyEventSellGroupingFilters = this.applyEventSellGroupingFilters.bind(this);
    this.selectAllCheckboxClick = this.selectAllCheckboxClick.bind(this);
    this.state = {
      sellGroupingFilters: {
        selectedEvents: {},
        categoryIsSports: false,
        performerId: null,
        venueId: null,
        venueConfigId: null,
        eventType: null
      }
    };
  }

  /**
   * ContentTypes are Required for Server Side Rendering
   *
   */
  static contextTypes = {...StoreProvider.childContextTypes};

  static propTypes = {
    id: PropTypes.any.isRequired,
    localStateId: PropTypes.any,
    // eslint-disable-next-line react/no-unused-prop-types
    total: PropTypes.number,
    events: PropTypes.array,
    loading: PropTypes.bool,
    defaultSort: PropTypes.string,
    sortOptions: PropTypes.array,
    setSort: PropTypes.func.isRequired,
    resetEvents: PropTypes.func,

    /**
     * Flag to Turn on the View Tickets Feature
     *
     */
    enableViewTickets: PropTypes.bool,

    /**
     * Options Containing the Parts of the Event Items to be Hidden
     *
     */
    eventItemDisplayOptions: PropTypes.object,

    /**
     * Flag to Disable the use of location to get the events
     *
     */
    // eslint-disable-next-line react/no-unused-prop-types
    disableGeoLocation: PropTypes.bool,

    /**
     * Flag to Disable the Pagination for the component
     *
     */
    disablePagination: PropTypes.bool,

    /**
     * Flag to Disable urgency for the current component instance at the API.
     *
     */
    // eslint-disable-next-line react/no-unused-prop-types
    disableUrgency: PropTypes.bool,

    /**
     * Class Name for Over-riding The Styles
     *
     */
    className: PropTypes.string,

    /**
     * Hide the Border around the Event Panel
     *
     */
    hideBorder: PropTypes.bool,

    /**
     * Store id
     */
    shstoreId: PropTypes.number,

    /**
     * Only allow specific number of events to be selected
     *
     */
    maxSelectedEvents: PropTypes.number,

    /**
     * If SSR
     */
    // eslint-disable-next-line react/no-unused-prop-types
    ssr: PropTypes.bool
  };

  static defaultProps = {
    ssr: true,
    disableGeoLocation: false,
    disablePagination: false,
    disableUrgency: false,
    maxSelectedEvents: Infinity
  };

  /**
   * Method to load event from the Search Catalog Event API
   * @param props [object] The object containing the `props` from the component.
   * @param reset [boolean] The flag denoting if the currently loaded events have to reset and load the event from beginning.
   *
   */
  loadEvents(props, reset) {
    props.globalRegistry = this.context.globalRegistry;
    // Sometimes when the props change and sort is empty, it does not send a value to the Search Catalog Event API
    const {searchParams = {}} = props;
    const {sort: spSort = ''} = searchParams;
    if (props.sort === '') {
      props.sort = spSort === '' ? 'eventDateLocal asc' : spSort;
    }

    return this.props.loadEvents(props, reset);
  }

  /**
   * Method called to initialize the events (while rendering the component in the server)
   * @param props [object] The object containing the `props` from the component.
   *
   */
  @ssr_wait
  init(props) {
    return this.loadEvents({...props, events: [], ...this.getSortValue(this.props.defaultSort)}, true);
  }

  /**
   * Callback Method used by the sort filter to trigger the call to Search Catalog Event API with selected search specs
   * @param option [object] The object containing the `sort option` from the component.
   *
   */
  sort = (option) => {
    let {key, value} = option;
    let opts = {...this.props, events: [], ...value};

    this.props.setSort(key);
    this.loadEvents(opts, true);
  };

  /**
   * Callback Method used by the sort filter to trigger the call to Search Catalog Event API to fetch next page of events.
   *
   */
  loadMore() {
    let props = {...this.props, ...this.getSortValue()};
    this.loadEvents(props);
  }

  /**
   * Method used to the Sort Value based on the selected sort key.
   * @param sortKey [number] The key associated to the selected sort option.
   *
   */
  getSortValue(sortKey) {
    let {sortOptions, defaultSort, sort} = this.props;
    sortKey = sortKey || sort || defaultSort;
    let value;
    sortOptions &&
      sortOptions.some((option) => {
        if (option.key === sortKey) {
          value = option.value;

          return true;
        }

        return false;
      });

    return value;
  }

  /**
   * React Lifecycle Methods
   *
   */
  UNSAFE_componentWillMount() {
    let {searchParams, localStateId, id, events} = this.props;

    /**
     * Re-initialize if the `events` are empty or if `localStateId` from redux state doesn't match the New `id` passed in props
     * NOTE - Section Handles the Route change while component is unmounted.
     *
     */
    if ((!events || !events.length || (id && localStateId !== id)) && searchParams) {
      this.init(this.props);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isSell) {
      this.preserveSellAppStateInfo(prevProps, this.props);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    /**
     * Re-initialize if the current `id` is not the same a `nextProps.id` (Route change)
     *
     */

    const {dateFilter: nextDateFilter, isMultipleEventToggleSelected: nextIsMultipleEventToggleSelected, parkingFilter: nextParkingFilter, searchParams: nextSearchParams = {}} = nextProps;

    const {dateFilter, isMultipleEventToggleSelected, parkingFilter, searchParams = {}} = this.props;

    const dateKey = dateFilter ? dateFilter.key : '';
    const nextDateKey = nextDateFilter ? nextDateFilter.key : '';

    /*
     * If the searchParams, parkingFilter, multiEventToggle, or dateFilter change,
     * reset all selected multi-events and app state for sell
     */
    if (searchParams.q !== nextSearchParams.q || nextParkingFilter !== parkingFilter || isMultipleEventToggleSelected !== nextIsMultipleEventToggleSelected || dateKey !== nextDateKey) {
      this.resetSellGroupingFilters();
    }

    if (this.props.id !== nextProps.id || searchParams.sort !== searchParams.nextSort || nextProps.reset === true) {
      return this.loadEvents({...nextProps, events: []}, true);
    }
  }

  preserveSellAppStateInfo(prevProps, props) {
    const {events, numSelectedEvents, parkingFilter} = props;

    /** If events/parking filter has changed , only then fetch from API again ; or just the first time when component mounts */
    if (prevProps.parkingFilter !== parkingFilter) {
      this.init(props);
    }

    /** The following block should be called only if events have loaded */
    if (events && events.length > 0) {
      /* Remove all disabled state from the events array */
      if (prevProps.parkingFilter !== parkingFilter || prevProps.events.length !== events.length || (prevProps.numSelectedEvents === 0 && prevProps.numSelectedEvents !== numSelectedEvents)) {
        events.forEach((event) => {
          event.isDisabled = false;
        });
      }
    }
  }

  componentWillUnmount() {
    let {resetEvents} = this.props;
    typeof resetEvents === 'function' && resetEvents();
  }

  getEventIsDisabled = (event) => {
    const {
      sellGroupingFilters: {categoryIsSports, performerId, venueId, venueConfigId, eventType}
    } = this.state;

    const {numSelectedEvents, maxSelectedEvents, isMultipleEventToggleSelected, isSell, shstoreId} = this.props;

    if (isSell && shstoreId === countryCodes.US && isMultipleEventToggleSelected) {
      const isNotUSEvent = countryCodes[event.venue.country] !== countryCodes.US;

      if (isNotUSEvent) {
        return isNotUSEvent;
      }
    }
    const {selectedEvents} = this.state.sellGroupingFilters;

    if (selectedEvents[event.id]) {
      return false;
    }

    if (categoryIsSports) {
      const getPerformer = event.performers ? event.performers[0].id : event.groupings && event.groupings[event.groupings.length - 1].id;

      return numSelectedEvents > 0 && (numSelectedEvents >= maxSelectedEvents || performerId !== getPerformer || venueId !== event.venue.id || eventType !== event.eventType || venueConfigId !== event.venueConfiguration.id);
    }

    return numSelectedEvents > 0 && (numSelectedEvents >= maxSelectedEvents || venueId !== event.venue.id || eventType !== event.eventType || venueConfigId !== event.venueConfiguration.id);
  };

  applyEventSellGroupingFilters = () => {
    return this.props.events.map((event) => {
      const isDisabled = this.getEventIsDisabled(event);

      return {...event, isDisabled};
    });
  };

  /**
   * Handle checkbox click for sell app multi-event listings
   * If checkbox is clicked, then we need to find events with same (performer ID - not required when category is Concert)
   * ,venue ID and eventType as the (checkbox) selected event
   * User should only be able to list similar events, hence we need to prevent users from listing for other events
   * Hence, need to set `isDisabled` prop to true for each event that does not match the above criteria
   *
   * @param isChecked - the checked state of the item's checkbox, event- the object for the selected event
   * @param event - an event object that is selected
   */
  handleEventItemCheckboxClick = (isChecked, event) => {
    const {updateSellEvents} = this.props;
    if (!isChecked) {
      this.setState(
        (prevState) => {
          return {
            sellGroupingFilters: {
              ...prevState.sellGroupingFilters,
              selectedEvents: {
                ...prevState.sellGroupingFilters.selectedEvents,
                [event.id]: isChecked
              }
            }
          };
        },
        () => {
          updateSellEvents(this.state.sellGroupingFilters.selectedEvents);
        }
      );

      /**
       * If Select All is checked,
       * then on unchecking even a single event item
       * should uncheck the Select All checkbox
       *
       */
      if (this.selectAllCheckbox.checked) {
        this.selectAllCheckbox.checked = false;
      }

      return;
    }
    const categoryIsSports = event.categories && event.categories.some((item) => item.name === 'Sports');
    const {selectedEvents} = this.state.sellGroupingFilters;
    const sellGroupingFilters = {
      categoryIsSports,
      performerId: event.performers ? event.performers[0].id : event.groupings && event.groupings[event.groupings.length - 1].id,
      venueId: event.venue && event.venue.id,
      venueConfigId: event.venueConfiguration && event.venueConfiguration.id,
      eventType: event.eventType,
      selectedEvents: {...selectedEvents, [event.id]: isChecked}
    };
    this.props.updateSellEvents(sellGroupingFilters.selectedEvents);
    this.setState({sellGroupingFilters});
  };

  /**
   * Select All Checkbox click for sell
   * On checkbox click, we need to traverse through all state events and non-disabled ones to selected events
   * @param isChecked - checked attribute of the checkbox element
   */
  selectAllCheckboxClick = (isChecked) => {
    const {events, maxSelectedEvents} = this.props;
    let {selectedEvents} = this.state.sellGroupingFilters;
    if (isChecked) {
      events.forEach((event) => {
        if (Object.keys(selectedEvents).filter((id) => selectedEvents[id]).length >= maxSelectedEvents) {
          return;
        }
        if (!this.getEventIsDisabled(event)) {
          selectedEvents = {...selectedEvents, [event.id]: true};
        }
      });
    } else {
      selectedEvents = {};
    }
    this.props.updateSellEvents(selectedEvents);
    this.setState((prevState) => {
      return {
        sellGroupingFilters: {
          ...prevState.sellGroupingFilters,
          selectedEvents,
          categoryIsSports: !isChecked ? false : prevState.sellGroupingFilters.categoryIsSports,
          performerId: !isChecked ? null : prevState.sellGroupingFilters.performerId,
          venueId: !isChecked ? null : prevState.sellGroupingFilters.venueId,
          venueConfigId: !isChecked ? null : prevState.sellGroupingFilters.venueConfigId,
          eventType: !isChecked ? null : prevState.sellGroupingFilters.eventType
        }
      };
    });
  };

  resetSellGroupingFilters = () => {
    const {updateSellEvents} = this.props;
    this.setState(
      {
        sellGroupingFilters: {
          selectedEvents: {},
          categoryIsSports: false,
          performerId: null,
          venueId: null,
          venueConfigId: null,
          eventType: null
        }
      },
      () => {
        updateSellEvents(this.state.sellGroupingFilters.selectedEvents);
      }
    );
  };

  /**
   * Select all ref -  this ref is used for setting the checked state of sell search results - Select All checkbox manually
   * @param checkbox - the actual checkbox element being passed from the child SellPanel
   *
   */
  selectAllRef = (checkbox) => {
    this.selectAllCheckbox = checkbox;
  };

  render() {
    const {className, disableMultipleEventsSwitch, disablePagination, enableViewTickets, eventItemDisplayOptions, hasMore, hideBorder, hidePanelHeader, isMultipleEventToggleSelected, isSell, loading, numFound = 0, numSelectedEvents, onToggleClicked, searchParams = {}, sort, sortOptions, shstoreId} = this.props;

    let {defaultSort = 'date', events, title} = this.props;

    if (this.props.isSell) {
      return <SellListPanelLayout events={this.applyEventSellGroupingFilters()} selectedEvents={this.state.sellGroupingFilters.selectedEvents} onEventItemCheckboxClick={this.handleEventItemCheckboxClick} onSelectAllCheckboxClick={this.selectAllCheckboxClick} onToggleClicked={onToggleClicked} isMultipleEventToggleSelected={isMultipleEventToggleSelected} isSelectAllChecked={this.selectAllCheckbox && this.selectAllCheckbox.checked ? this.selectAllCheckbox.checked : false} selectAllRef={this.selectAllRef} loadMore={this.loadMore.bind(this)} numSelectedEvents={numSelectedEvents} numFound={numFound} loading={loading} hasMore={hasMore} enableViewTickets={enableViewTickets} eventItemDisplayOptions={eventItemDisplayOptions} disablePagination={disablePagination} disableMultipleEventsSwitch={disableMultipleEventsSwitch} className={className} hideBorder={hideBorder} storeId={shstoreId} isSell={isSell} />;
    }

    const firstLoading = numFound === 0 && loading;
    const {key: spSort} = searchParams;
    if (sort || spSort) {
      defaultSort = sort || spSort;
    }

    /**
     * Show the Filter only if the Filter Options are Passed externally from the Parent
     *
     */
    const showSortFilter = sortOptions && sortOptions.length;

    /**
     * If title is passed as a function then execute it with current props before using the Title
     *
     */
    title = typeof title === 'function' ? title(this.props) : title;

    /**
     * Header Section
     *
     */
    const header =
      (title || showSortFilter) && !hidePanelHeader ? (
        <div className="EventListPanel__Header">
          <div className="EventListPanel__HeaderText">{title}</div>
          <div className="EventListPanel__SorterContainer">{showSortFilter ? <EventSorter onSelection={(option) => this.sort(option)} selected={defaultSort} items={sortOptions} /> : null}</div>
        </div>
      ) : null;

    /**
     * Footer Section
     *
     */
    const footer =
      !disablePagination && hasMore ? (
        <FormattedLink className="EventListPanel__Footer" onClick={this.loadMore.bind(this)}>
          {loading ? loadingMessageText : seeMoreEventsText}
        </FormattedLink>
      ) : null;

    const eventList = <EventList className="EventResult" events={events} threshold={100} enableViewTickets={enableViewTickets} eventItemDisplayOptions={eventItemDisplayOptions} />;

    return !numFound ? null : firstLoading ? loadingMessageText : <Panel className={cs(className || 'EventListPanel')} footer={footer} header={header} content={eventList} border={!hideBorder} />;
  }
}

export default urgency(EventListPanel);
export {default as container} from './container';
